import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Building2, Check, ChevronsUpDown, Plus, User } from "@fleet/icons";
import { cn } from "@stockpilot/shared";
import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TextField } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { useActiveOrg } from "@/features/organizations/hooks/use-active-org";

/**
 * Organization selector (company / supermarket) in the header — pattern of the
 * account-switcher from the template but for orgs.
 *
 * Instantiates ITS OWN `useActiveOrg`; shares the active org with routes
 * through the module signal of `active-org.ts`, so switching here reactively
 * rescopes ALL the WMS (catalog, warehouses, stock, operations,
 * purchases, reports…) without reloading or prop-drilling.
 *
 * Always present: the "Personal" workspace (inventory `organization_id =
 * user_id`) is an additional entry, so a user without companies sees Personal +
 * "Add organization" (entry point to create their first supermarket). A visitor
 * cannot create orgs (server returns 401); creation catches this and renders the
 * error inline (CC-30) instead of leaving the button stuck.
 *
 * "Add organization" reuses `bootstrapOrg` (atomic server-side creation + wait
 * for sync + activation), with inline error feedback (CC-30 / family CC-8:
 * never leave the button stuck without feedback).
 */
export function OrgSwitcher() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const session = useSession();
  const ctx = useActiveOrg();
  const [open, setOpen] = createSignal(false);
  const [adding, setAdding] = createSignal(false);
  const [name, setName] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const canCreate = () => !session.isVisitor();
  const canSubmit = () => Boolean(db()) && name().trim().length > 0 && !busy() && canCreate();

  const choose = (scopeId: string) => {
    ctx.setActiveOrg(scopeId);
    setOpen(false);
  };

  const resetAddForm = () => {
    setAdding(false);
    setName("");
    setError(null);
  };

  async function createOrg(e: Event) {
    e.preventDefault();
    if (!canSubmit()) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // `bootstrapOrg` already sets the new organization as ACTIVE.
      await ctx.bootstrapOrg(name());
      resetAddForm();
      setOpen(false);
    } catch (err) {
      console.error("[OrgSwitcher] add organization failed:", err);
      setError(t(msg`We couldn't create your organization. Check your connection and try again.`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Popover
      open={open()}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          resetAddForm();
        }
      }}
    >
      <PopoverTrigger
        as="button"
        type="button"
        data-testid="org-switcher"
        class={cn(
          buttonVariants({ variant: "ghost", size: "sm" }),
          "h-9 max-w-[12rem] gap-2 px-2 text-sm font-medium text-muted-foreground hover:text-foreground",
        )}
        title={t(msg`Switch organization`)}
        aria-label={t(msg`Switch organization`)}
      >
        <Show when={!ctx.activeScope().personal} fallback={<User class="h-4 w-4 shrink-0" />}>
          <Building2 class="h-4 w-4 shrink-0" />
        </Show>
        <span class="truncate" data-testid="org-switcher-current">
          {ctx.activeScope().name}
        </span>
        <ChevronsUpDown class="h-3.5 w-3.5 shrink-0 opacity-60" />
      </PopoverTrigger>
      <PopoverContent class="w-64 p-1.5">
        <p class="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t(msg`Organizations`)}
        </p>
        <div class="max-h-[40vh] space-y-0.5 overflow-y-auto">
          <For each={ctx.scopes()}>
            {(scope) => {
              const isActive = () => scope.id === ctx.activeScopeId();
              return (
                <button
                  type="button"
                  data-testid="org-switcher-item"
                  data-active={isActive()}
                  data-personal={scope.personal}
                  class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/50 data-[active=true]:bg-accent/40 data-[active=true]:font-semibold"
                  onClick={() => choose(scope.id)}
                >
                  <Show
                    when={!scope.personal}
                    fallback={<User class="h-4 w-4 shrink-0 opacity-70" />}
                  >
                    <Building2 class="h-4 w-4 shrink-0 opacity-70" />
                  </Show>
                  <span class="flex-1 truncate">{scope.name}</span>
                  <Show when={isActive()}>
                    <Check class="h-4 w-4 shrink-0 text-primary" />
                  </Show>
                </button>
              );
            }}
          </For>
        </div>

        <Show when={canCreate()}>
          <div class="my-1 h-px bg-border/60" />

          <Show
            when={adding()}
            fallback={
              <button
                type="button"
                data-testid="org-switcher-add"
                class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/50"
                onClick={() => setAdding(true)}
              >
                <Plus class="h-4 w-4 shrink-0" />
                <span>{t(msg`Add organization`)}</span>
              </button>
            }
          >
            <form class="space-y-2 p-1" onSubmit={createOrg}>
              <TextField
                data-testid="org-switcher-name-input"
                placeholder={t(msg`Company name`)}
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                autofocus
              />
              <div class="flex gap-2">
                <Button
                  data-testid="org-switcher-create"
                  type="submit"
                  size="sm"
                  class="flex-1"
                  disabled={!canSubmit()}
                >
                  {busy() ? t(msg`Creating…`) : t(msg`Create`)}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={resetAddForm}
                  disabled={busy()}
                >
                  {t(msg`Cancel`)}
                </Button>
              </div>
              <Show when={error()}>
                {(message) => (
                  <p role="alert" data-testid="org-switcher-error" class="text-xs text-destructive">
                    {message()}
                  </p>
                )}
              </Show>
            </form>
          </Show>
        </Show>
      </PopoverContent>
    </Popover>
  );
}
